import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { IoChevronBack, IoCartOutline, IoAdd, IoRemove } from 'react-icons/io5';
import { IoInformationCircleOutline } from 'react-icons/io5';
import Spinner from '../../components/common/Spinner';
import { useCart } from '../../context/CartContext';
import { useProducts } from '../../context/ProductContext';

const COLORS = {
  primary: '#4A6741',
  header: '#2C3640',
};


const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { products, loading } = useProducts();
  const product = products.find(p => p.id === productId) || null;
  const [qty, setQty] = useState(1);
  const [optionQtys, setOptionQtys] = useState({}); // { "10kg": 4, "20kg": 3 }
  const [memo, setMemo] = useState('');
  const [toast, setToast] = useState(null);

  const hasOptions = product?.options && product.options.length > 0;
  // 옵션값 정규화: string → { value, soldOut } 호환
  const normalizeValues = (values) => (values || []).map(v => typeof v === 'string' ? { value: v, soldOut: false } : v);
  const totalOptionQty = Object.values(optionQtys).reduce((s, v) => s + v, 0);

  const setOptQty = (val, delta) => {
    setOptionQtys(prev => {
      const cur = prev[val] || 0;
      const next = Math.max(0, cur + delta);
      const copy = { ...prev };
      if (next === 0) delete copy[val];
      else copy[val] = next;
      return copy;
    });
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <BackBtn onClick={() => navigate(-1)}><IoChevronBack size={22} /></BackBtn>
          <HeaderTitle>상품 상세</HeaderTitle>
          <Spacer />
        </Header>
        <Spinner variant="dots" size="md" text="상품 불러오는 중..." fullPage />
      </Container>
    );
  }

  if (!product) {
    return (
      <Container>
        <Header>
          <BackBtn onClick={() => navigate(-1)}><IoChevronBack size={22} /></BackBtn>
          <HeaderTitle>상품 없음</HeaderTitle>
          <Spacer />
        </Header>
        <EmptyState>상품을 찾을 수 없습니다</EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackBtn onClick={() => navigate(-1)}><IoChevronBack size={22} /></BackBtn>
        <HeaderTitle>상품 상세</HeaderTitle>
        <Spacer />
      </Header>

      {/* 대표 이미지 */}
      {product.image ? (
        <HeroImgWrap>
          <HeroImg src={product.image} alt={product.name} onLoad={e => e.target.style.opacity = 1} />
          {product.soldOut && <HeroSoldOut>품절</HeroSoldOut>}
        </HeroImgWrap>
      ) : (
        <HeroThumb>{product.name.slice(0, 3)}</HeroThumb>
      )}

      {/* 상품명 + 카테고리 */}
      <NameArea>
        <ProductName>{product.name}</ProductName>
        <CategoryRow>
          <CategoryTag>{product.category1 || product.category || ''}</CategoryTag>
          {product.category2 && <CategoryTag $sub>{product.category2}</CategoryTag>}
        </CategoryRow>
      </NameArea>

      {/* 옵션 + 수량 + 메모 */}
      <OrderSection>
        {hasOptions ? (
          product.options.map((opt, i) => {
            const values = normalizeValues(opt.values);
            return (
            <React.Fragment key={i}>
              <OrderRow>
                <OrderRowLabel>{opt.name}</OrderRowLabel>
              </OrderRow>
              {values.map((item, vi) => (
                <OptionRow key={vi} $disabled={item.soldOut}>
                  <OptionName $soldOut={item.soldOut}>
                    {item.value}
                    {item.soldOut && <OptionSoldOutTag>품절</OptionSoldOutTag>}
                  </OptionName>
                  {!item.soldOut ? (
                    <QtyControl>
                      <QtyBtn onClick={() => setOptQty(item.value, -1)}><IoRemove size={16} /></QtyBtn>
                      <QtyNum>{optionQtys[item.value] || 0}</QtyNum>
                      <QtyBtn onClick={() => setOptQty(item.value, 1)}><IoAdd size={16} /></QtyBtn>
                    </QtyControl>
                  ) : (
                    <QtyControl style={{ opacity: 0.3, pointerEvents: 'none' }}>
                      <QtyBtn><IoRemove size={16} /></QtyBtn>
                      <QtyNum>0</QtyNum>
                      <QtyBtn><IoAdd size={16} /></QtyBtn>
                    </QtyControl>
                  )}
                </OptionRow>
              ))}
            </React.Fragment>
            );
          })
        ) : (
          <OrderRow>
            <OrderRowLabel>수량</OrderRowLabel>
            <QtyControl>
              <QtyBtn onClick={() => setQty(q => Math.max(1, q - 1))}><IoRemove size={18} /></QtyBtn>
              <QtyNum>{qty}</QtyNum>
              <QtyBtn onClick={() => setQty(q => q + 1)}><IoAdd size={18} /></QtyBtn>
            </QtyControl>
          </OrderRow>
        )}

        <OrderRow $column>
          <OrderRowLabel>메모</OrderRowLabel>
          <MemoInput
            placeholder="요청사항을 입력하세요"
            value={memo}
            onChange={e => setMemo(e.target.value)}
            rows={5}
          />
        </OrderRow>
      </OrderSection>

      {/* 하단 장바구니 버튼 */}
      <BottomBar>
        <OrderBtn
          disabled={product.soldOut || (hasOptions && totalOptionQty === 0) || (hasOptions && product.options.every(o => normalizeValues(o.values).every(v => v.soldOut)))}
          onClick={() => {
            if (product.soldOut) return;
            const cartMemo = memo.trim() || undefined;
            if (hasOptions) {
              addToCart({ ...product, optionQtys, memo: cartMemo }, totalOptionQty);
              setToast({ name: product.name, qty: totalOptionQty });
            } else {
              addToCart({ ...product, memo: cartMemo }, qty);
              setToast({ name: product.name, qty });
            }
            setTimeout(() => setToast(null), 2500);
          }}
        >
          <IoCartOutline size={20} /> {product.soldOut || (hasOptions && product.options.every(o => normalizeValues(o.values).every(v => v.soldOut))) ? '품절된 상품입니다' : `장바구니 담기${hasOptions && totalOptionQty > 0 ? ` (${totalOptionQty}개)` : !hasOptions ? ` (${qty}개)` : ''}`}
        </OrderBtn>
      </BottomBar>

      {toast && (
        <Toast>
          <IoInformationCircleOutline size={18} color="#4A6741" />
          <ToastText>
            <ToastLine1>{toast.name}</ToastLine1>
            <ToastLine2>{toast.qty}개 장바구니에 담겼습니다</ToastLine2>
          </ToastText>
        </Toast>
      )}
    </Container>
  );
};

export default ProductDetailPage;

const Container = styled.div`
  min-height: 100vh;
  background: white;
  padding-bottom: 80px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 14px;
  background: white;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const BackBtn = styled.button`
  display: flex;
  padding: 4px;
  color: ${COLORS.header};
`;

const HeaderTitle = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #1E2A32;
`;

const Spacer = styled.div`width: 30px;`;

const EmptyState = styled.p`
  text-align: center;
  padding: 60px 20px;
  font-size: 14px;
  color: #bbb;
`;

const HeroImgWrap = styled.div`
  width: 100%;
  aspect-ratio: 1;
  position: relative;
  background: #ECEEE9;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%);
    animation: shimmer 1.5s infinite;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const HeroImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: relative;
  z-index: 1;
  background: #F5F5F0;
  opacity: 0;
  transition: opacity 0.4s ease;
`;

const HeroSoldOut = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 4px;
`;

const HeroThumb = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: #EDF2EB;
  color: ${COLORS.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 700;
`;

const NameArea = styled.div`padding: 20px;`;

const ProductName = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: #1E2A32;
  line-height: 1.4;
`;

const CategoryRow = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const CategoryTag = styled.span`
  font-size: 12px;
  color: ${p => p.$sub ? COLORS.primary : '#8C9AA4'};
  background: ${p => p.$sub ? '#EDF2EB' : '#F0F0F0'};
  padding: 4px 12px;
  border-radius: 10px;
  font-weight: 500;
`;

/* 옵션 + 수량 + 메모 통합 섹션 */
const OrderSection = styled.div`
  margin: 0 16px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #F0F0EE;
  overflow: hidden;
`;

const OrderRow = styled.div`
  display: flex;
  align-items: ${p => p.$column ? 'flex-start' : 'center'};
  flex-direction: ${p => p.$column ? 'column' : 'row'};
  justify-content: space-between;
  gap: ${p => p.$column ? '10px' : '12px'};
  padding: 16px 20px;
  &:not(:last-child) { border-bottom: 1px solid #F0F0EE; }
`;

const OrderRowLabel = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #1E2A32;
  flex-shrink: 0;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  opacity: ${p => p.$disabled ? 0.5 : 1};
  background: ${p => p.$disabled ? '#FAFAFA' : 'transparent'};
  &:not(:last-child) { border-bottom: 1px solid #F0F0EE; }
`;

const OptionName = styled.span`
  font-size: 14px;
  color: #1E2A32;
  text-decoration: ${p => p.$soldOut ? 'line-through' : 'none'};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const OptionSoldOutTag = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #D32F2F;
  background: #FFEBEE;
  padding: 1px 6px;
  border-radius: 4px;
`;

const MemoInput = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #E0E0E0;
  border-radius: 10px;
  font-size: 14px;
  color: #1E2A32;
  resize: vertical;
  font-family: inherit;
  &:focus { border-color: ${COLORS.primary}; outline: none; }
  &::placeholder { color: #B0B8C0; }
`;

/* 하단 바 */
const BottomBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  padding: 12px 16px;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
  background: white;
  border-top: 1px solid #EEEEF0;
  z-index: 10;
  display: flex;
  gap: 10px;
`;

const QtyControl = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  background: #F0F0EC;
  border-radius: 12px;
  padding: 0 4px;
`;

const QtyBtn = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1E2A32;
`;

const QtyNum = styled.span`
  font-size: 15px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  color: #1E2A32;
`;

const OrderBtn = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  background: ${({ disabled }) => disabled ? '#D0D0D0' : COLORS.primary};
  color: white;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s;
  pointer-events: ${({ disabled }) => disabled ? 'none' : 'auto'};
`;

const toastSlideUp = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(20px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

const Toast = styled.div`
  position: fixed;
  bottom: 90px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border-radius: 12px;
  padding: 14px 20px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  z-index: 100;
  min-width: 270px;
  max-width: 340px;
  animation: ${toastSlideUp} 0.3s ease;
`;

const ToastText = styled.div`
  flex: 1;
`;

const ToastLine1 = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: #1E2A32;
  line-height: 1.4;
  word-break: keep-all;
`;

const ToastLine2 = styled.p`
  font-size: 13px;
  color: #5A6B75;
  margin-top: 2px;
`;
